<template>
  <nav
    v-if="items.length > 0"
    class="context-section-strip"
    :class="scrollEdgeClasses"
    :aria-label="ariaLabel"
    data-testid="context-section-strip"
  >
    <div ref="scroller" class="context-section-strip__scroller">
      <router-link
        v-for="item in items"
        :key="item.key"
        :to="item.to"
        class="context-section-strip__item"
        :class="{ 'context-section-strip__item--active': item.key === activeKey }"
        :aria-current="item.key === activeKey ? 'page' : undefined"
        :data-testid="`context-section-strip-${item.key}`"
      >
        <component :is="item.icon" class="context-section-strip__icon" :size="16" />
        <span>{{ t(item.labelKey) }}</span>
      </router-link>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, useTemplateRef, watch, type Component } from 'vue'
import { useI18n } from 'vue-i18n'
import type { RouteLocationRaw } from 'vue-router'
import { useDossierContext } from '@/composables/useDossierContext'
import { useOrganizationSettingsNav } from '@/composables/useOrganizationAreas'
import { revealInScroller, useHorizontalOverflow } from '@/composables/useHorizontalOverflow'

/**
 * The section navigation of a client dossier or of Organisatiebeheer, as a
 * horizontal strip for the screens where the sidebar is a drawer behind the
 * hamburger. There the sidebar is out of sight, so without this nothing on the
 * page offers the way to a sibling section or says which one is open.
 *
 * It reads the same declarations the sidebar does, so the two cannot offer
 * different sections. At `lg-up` the sidebar is on screen and this hides.
 */

interface StripItem {
  key: string
  labelKey: string
  icon: Component
  to: RouteLocationRaw
}

const { t } = useI18n()

const { isActive: isDossierOpen, client, activeView, navItems } = useDossierContext()
const { isActive: isSettingsOpen, activeAreaId, groups } = useOrganizationSettingsNav()

const items = computed<StripItem[]>(() => {
  if (isDossierOpen.value) {
    const clientId = client.value?.id
    if (!clientId) return []
    return navItems.value.map((item) => ({
      key: item.key,
      labelKey: item.labelKey,
      icon: item.icon,
      to: { name: item.routeName, params: { clientId, ...item.params } }
    }))
  }
  if (isSettingsOpen.value) {
    return groups.value.flatMap((group) =>
      group.areas.map((area) => ({
        key: area.id,
        labelKey: area.titleKey,
        icon: area.icon,
        to: { name: area.routeName }
      }))
    )
  }
  return []
})

const activeKey = computed(() => (isDossierOpen.value ? activeView.value : activeAreaId.value))

const ariaLabel = computed(() =>
  t(isDossierOpen.value ? 'clientDossier.nav.label' : 'breadcrumbs.organizationManagement')
)

const scroller = useTemplateRef<HTMLElement>('scroller')

const { canScrollLeft, canScrollRight } = useHorizontalOverflow(scroller)
const scrollEdgeClasses = computed(() => ({
  'context-section-strip--more-left': canScrollLeft.value,
  'context-section-strip--more-right': canScrollRight.value
}))

function revealActiveItem() {
  const el = scroller.value
  const item = el?.querySelector<HTMLElement>('.context-section-strip__item--active')
  if (el && item) revealInScroller(el, item)
}

onMounted(revealActiveItem)
watch([activeKey, items], () => nextTick(revealActiveItem))
</script>

<style scoped>
.context-section-strip {
  display: none;
}

@media (--breakpoint-lg-down) {
  .context-section-strip {
    display: block;
    position: relative;
    background: var(--color-background-content);
    border-bottom: 1px solid var(--color-border-default);
  }
}

.context-section-strip::before,
.context-section-strip::after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  width: var(--spacing-lg);
  pointer-events: none;
  opacity: 0;
  transition: opacity var(--transition-duration-fast) var(--transition-easing-ease-out);
}

.context-section-strip::before {
  left: 0;
  background: linear-gradient(to right, var(--color-overlay-scroll-edge), transparent);
}

.context-section-strip::after {
  right: 0;
  background: linear-gradient(to left, var(--color-overlay-scroll-edge), transparent);
}

.context-section-strip--more-left::before,
.context-section-strip--more-right::after {
  opacity: 1;
}

/* Positioned, so an item's offsetLeft is measured from the strip it scrolls in. */
.context-section-strip__scroller {
  position: relative;
  display: flex;
  gap: var(--spacing-xs);
  padding: 0 var(--spacing-lg);
  overflow-x: auto;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
}

.context-section-strip__item {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  flex-shrink: 0;
  min-height: var(--sizing-touch-target-min);
  padding: 0 var(--spacing-md);
  white-space: nowrap;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-secondary);
  text-decoration: none;
  border-bottom: 2px solid transparent;
  transition: color var(--transition-duration-fast) var(--transition-easing-ease-out),
    border-color var(--transition-duration-fast) var(--transition-easing-ease-out);
}

.context-section-strip__item:hover {
  color: var(--color-text-primary);
}

.context-section-strip__item--active {
  color: var(--color-brand-primary);
  border-bottom-color: var(--color-brand-primary);
}

.context-section-strip__icon {
  flex-shrink: 0;
}
</style>
